/**
 * LibrarySync - 라이브러리 동기화 컴포넌트
 *
 * 1. JSON 내보내기/가져오기 (백업용)
 * 2. QR 코드로 기기간 이동 (간편 이동)
 *
 * 홍익인간 (Hongik Ingan) - WIA Standards
 */

import { useState, useRef } from 'react';
import { useStore } from '../../store/useStore';
import { useI18n } from '../../i18n';
import {
  FiDownload,
  FiUpload,
  FiSmartphone,
  FiX,
  FiCheck,
  FiAlertCircle,
} from 'react-icons/fi';

interface LibrarySyncProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ExportData {
  version: string;
  exportedAt: string;
  data: {
    books: any[];
    highlights: any[];
    notes: any[];
    readerSettings: any;
  };
}

const LibrarySync = ({ isOpen, onClose }: LibrarySyncProps) => {
  const { t } = useI18n();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [showQR, setShowQR] = useState(false);
  const [qrData, setQrData] = useState('');

  const { books, highlights, notes, readerSettings, addBook } = useStore();

  // 📤 라이브러리 내보내기 (JSON)
  const handleExport = () => {
    try {
      const exportData: ExportData = {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        data: {
          books: books.map((book) => ({
            ...book,
            // Blob URL은 제외 (다른 기기에서 사용 불가)
            cover: book.cover?.startsWith('data:') ? book.cover : null,
          })),
          highlights,
          notes,
          readerSettings,
        },
      };

      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `wiabooks-library-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setStatus('success');
      setMessage(t('sync.exportSuccess') || '라이브러리가 성공적으로 내보내졌습니다!');
      setTimeout(() => setStatus('idle'), 3000);
    } catch (error) {
      setStatus('error');
      setMessage(t('sync.exportError') || '내보내기 중 오류가 발생했습니다.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  // 📥 라이브러리 가져오기 (JSON)
  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importData: ExportData = JSON.parse(e.target?.result as string);

        // 버전 체크
        if (!importData.version || !importData.data) {
          throw new Error('Invalid file format');
        }

        // 책 가져오기 (중복 제외)
        const existingIds = new Set(books.map((b) => b.id));
        const newBooks = importData.data.books.filter((b: any) => !existingIds.has(b.id));

        newBooks.forEach((book: any) => {
          addBook(book);
        });

        // 하이라이트, 노트는 store에서 직접 병합하려면 추가 로직 필요
        // 현재는 책 정보만 가져오기

        setStatus('success');
        setMessage(
          t('sync.importSuccess', { count: newBooks.length }) ||
            `${newBooks.length}권의 책을 가져왔습니다!`
        );
        setTimeout(() => setStatus('idle'), 3000);
      } catch (error) {
        setStatus('error');
        setMessage(t('sync.importError') || '가져오기 중 오류가 발생했습니다. 파일 형식을 확인하세요.');
        setTimeout(() => setStatus('idle'), 3000);
      }
    };
    reader.readAsText(file);

    // 파일 입력 초기화
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 📱 QR 코드 생성 (기기간 이동)
  const handleGenerateQR = () => {
    try {
      // 라이브러리 데이터를 압축해서 URL로 인코딩
      const syncData = {
        v: '1',
        b: books.slice(0, 10).map((book) => ({
          id: book.id,
          title: book.title,
          author: book.author,
          progress: book.progress,
        })),
      };

      // Base64 인코딩
      const encoded = btoa(encodeURIComponent(JSON.stringify(syncData)));

      // 현재 URL 기반으로 동기화 URL 생성
      const baseUrl = window.location.origin + window.location.pathname;
      const syncUrl = `${baseUrl}?sync=${encoded}`;

      // QR Server API 사용 (외부 라이브러리 없이)
      const qrImageUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(syncUrl)}`;

      setQrData(qrImageUrl);
      setShowQR(true);
    } catch (error) {
      setStatus('error');
      setMessage('QR 코드 생성 중 오류가 발생했습니다.');
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('sync.title') || '📚 라이브러리 동기화'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
          >
            <FiX size={20} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Status Message */}
          {status !== 'idle' && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                status === 'success'
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                  : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
              }`}
            >
              {status === 'success' ? <FiCheck size={20} /> : <FiAlertCircle size={20} />}
              <span className="text-sm">{message}</span>
            </div>
          )}

          {/* QR Code Display */}
          {showQR ? (
            <div className="text-center space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {t('sync.qrInstruction') || '다른 기기에서 이 QR 코드를 스캔하세요'}
              </p>
              <div className="flex justify-center">
                <img
                  src={qrData}
                  alt="Library Sync QR Code"
                  className="w-48 h-48 rounded-lg border-4 border-primary"
                />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                {t('sync.qrNote') || '※ 최근 10권의 책 정보가 포함됩니다'}
              </p>
              <button
                onClick={() => setShowQR(false)}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {t('common.close') || '닫기'}
              </button>
            </div>
          ) : (
            <>
              {/* Export Button */}
              <button
                onClick={handleExport}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl hover:shadow-lg transition-all hover:scale-[1.02]"
              >
                <div className="p-3 bg-white/20 rounded-lg">
                  <FiDownload size={24} />
                </div>
                <div className="text-left">
                  <h3 className="font-bold">{t('sync.export') || '📤 내보내기'}</h3>
                  <p className="text-sm text-white/80">
                    {t('sync.exportDesc') || 'JSON 파일로 라이브러리 백업'}
                  </p>
                </div>
              </button>

              {/* Import Button */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:shadow-lg transition-all hover:scale-[1.02]"
              >
                <div className="p-3 bg-white/20 rounded-lg">
                  <FiUpload size={24} />
                </div>
                <div className="text-left">
                  <h3 className="font-bold">{t('sync.import') || '📥 가져오기'}</h3>
                  <p className="text-sm text-white/80">
                    {t('sync.importDesc') || 'JSON 파일에서 라이브러리 복원'}
                  </p>
                </div>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleImport}
                className="hidden"
              />

              {/* QR Code Button */}
              <button
                onClick={handleGenerateQR}
                className="w-full flex items-center gap-4 p-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl hover:shadow-lg transition-all hover:scale-[1.02]"
              >
                <div className="p-3 bg-white/20 rounded-lg">
                  <FiSmartphone size={24} />
                </div>
                <div className="text-left">
                  <h3 className="font-bold">{t('sync.qrTransfer') || '📱 QR로 이동'}</h3>
                  <p className="text-sm text-white/80">
                    {t('sync.qrTransferDesc') || '다른 기기로 간편하게 전송'}
                  </p>
                </div>
              </button>
            </>
          )}
        </div>

        {/* Footer Info */}
        <div className="px-6 pb-6">
          <div className="p-3 bg-gray-100 dark:bg-gray-700/50 rounded-lg">
            <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
              💡 {t('sync.tip') || '팁: 정기적으로 백업하면 데이터를 안전하게 보관할 수 있어요!'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LibrarySync;
